import { createContext, useContext, useCallback, useMemo } from 'react';
import type { ReactNode, ComponentType } from 'react';
import {
  SnackbarProvider as NotistackProvider,
  SnackbarKey,
  useSnackbar as useNotistackSnackbar,
  SnackbarMessage,
} from 'notistack';
import { GlobalStyles } from '@mui/material';
import { Box } from '../../Layout/Box';
import { CircularIconButton } from '../../Buttons';
import { theme } from '../../Theme/theme';
import { getSafeAreaTop } from '../../Theme/constants';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { Error as ErrorIcon, Close as CloseIcon } from '@mui/icons-material';
import type {
  CustomSnackbarOptions,
  CustomSnackbarContextType,
  SnackbarProviderProps,
} from './types';

const CustomSnackbarContext = createContext<CustomSnackbarContextType | null>(null);

/**
 * The close button is an inverted chip: its background is the snackbar's text colour
 * (white, or black on warning) and the icon inside is the snackbar's background colour.
 * `currentColor` gives us the background for free; the icon colour is per-variant and
 * therefore lives in the global styles below, keyed off `CLOSE_BUTTON_CLASS`.
 */
const CLOSE_BUTTON_CLASS = 'snackbar-close-button';

/**
 * CircularIconButton's 'sm' variant is the close-only size: a 24px circle around a 16px
 * icon, which already meets WCAG 2.2 SC 2.5.8. `color: inherit` is what makes the
 * inversion work — it lets `currentColor` (the background, set in the global styles)
 * resolve to the snackbar's text colour. The ::after grows the hit area to 44px for touch
 * without changing the visuals; the button is last in the row, so it covers nothing else.
 */
const closeButtonBaseSx = {
  color: 'inherit',
  boxShadow: 'none',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: '-10px',
  },
} as const;

const closeButtonSx = closeButtonBaseSx;
const closeButtonWithMarginSx = { ...closeButtonBaseSx, ml: 0.5 } as const;

/**
 * Hook to show and close snackbar notifications. Must be used within a SnackbarProvider.
 * Use variant: 'success' | 'error' | 'warning' | 'info'.
 */
export function useSnackbar(): CustomSnackbarContextType {
  const context = useContext(CustomSnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
}

function CustomSnackbarProvider({ children }: { children: ReactNode }) {
  const { enqueueSnackbar: originalEnqueueSnackbar, closeSnackbar } = useNotistackSnackbar();

  const defaultAction = useCallback(
    (key: SnackbarKey) => (
      <CircularIconButton
        size="sm"
        className={CLOSE_BUTTON_CLASS}
        onClick={() => closeSnackbar(key)}
        sx={closeButtonSx}
        aria-label="Close"
      >
        <CloseIcon />
      </CircularIconButton>
    ),
    [closeSnackbar]
  );

  const enqueueSnackbar = useCallback(
    (message: SnackbarMessage, options?: CustomSnackbarOptions) => {
      const { showAction = true, action: customAction, ...restOptions } = options ?? {};

      let finalAction: (key: SnackbarKey) => ReactNode;
      if (showAction === false) {
        finalAction = customAction
          ? (key: SnackbarKey) =>
              typeof customAction === 'function' ? customAction(key) : customAction
          : () => null;
      } else if (customAction) {
        finalAction = (key: SnackbarKey) => (
          <>
            {typeof customAction === 'function' ? customAction(key) : customAction}
            <CircularIconButton
              size="sm"
              aria-label="Close"
              className={CLOSE_BUTTON_CLASS}
              onClick={() => closeSnackbar(key)}
              sx={closeButtonWithMarginSx}
            >
              <CloseIcon />
            </CircularIconButton>
          </>
        );
      } else {
        finalAction = defaultAction;
      }

      return originalEnqueueSnackbar(message, { ...restOptions, action: finalAction });
    },
    [closeSnackbar, defaultAction, originalEnqueueSnackbar]
  );

  const contextValue = useMemo(
    () => ({ enqueueSnackbar, closeSnackbar }),
    [enqueueSnackbar, closeSnackbar]
  );

  return (
    <CustomSnackbarContext.Provider value={contextValue}>{children}</CustomSnackbarContext.Provider>
  );
}

type SnackbarIconProps = {
  icon: ComponentType<{ sx?: object }>;
};

function SnackbarIcon({ icon: IconComponent }: SnackbarIconProps) {
  return (
    <Box
      component="span"
      sx={{
        mr: 1.5,
        width: 24,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <IconComponent sx={{ width: 24, height: 24 }} />
    </Box>
  );
}

function StyledNotistack() {
  return (
    <GlobalStyles
      styles={{
        '.notistack-SnackbarContainer': {
          top: `calc(14px + ${getSafeAreaTop()}) !important`,
        },
        '.notistack-MuiContent': {
          width: '100%',
          padding: `${theme.spacing(1)} !important`,
          margin: `${theme.spacing(0.25, 0)} !important`,
          boxShadow: '0 8px 16px 0 rgba(0, 0, 0, 0.16) !important',
          borderRadius: '15px !important',
          color: `${theme.palette.common.white} !important`,
          backgroundColor: `${theme.palette.common.darkGray} !important`,
          display: 'flex !important',
          alignItems: 'flex-start !important',
          flexWrap: 'nowrap !important',
          gap: `${theme.spacing(1)} !important`,
          [theme.breakpoints.up('md')]: {
            minWidth: '240px',
          },
          '& > div:first-of-type': {
            padding: '0 !important',
            ...theme.typography.subtitle1,
            flex: '1 1 auto',
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            wordBreak: 'break-word',
          },
          '& > div:last-of-type': {
            marginRight: '0 !important',
            marginLeft: '0 !important',
            paddingLeft: '0 !important',
            flex: '0 0 auto',
            alignSelf: 'flex-start',
            display: 'flex',
            alignItems: 'center',
          },
          [`& .${CLOSE_BUTTON_CLASS}`]: {
            backgroundColor: 'currentColor !important',
            borderRadius: '50%',
          },
          [`& .${CLOSE_BUTTON_CLASS} svg`]: {
            color: theme.palette.common.darkGray,
          },
        },
        '.notistack-MuiContent-success': {
          color: `${theme.palette.getContrastText(theme.palette.success.main)} !important`,
          backgroundColor: `${theme.palette.success.main} !important`,
          [`& .${CLOSE_BUTTON_CLASS} svg`]: {
            color: theme.palette.success.main,
          },
        },
        '.notistack-MuiContent-error': {
          color: `${theme.palette.getContrastText(theme.palette.error.main)} !important`,
          backgroundColor: `${theme.palette.error.main} !important`,
          [`& .${CLOSE_BUTTON_CLASS} svg`]: {
            color: theme.palette.error.main,
          },
        },
        '.notistack-MuiContent-warning': {
          color: `${theme.palette.getContrastText(theme.palette.warning.main)} !important`,
          backgroundColor: `${theme.palette.warning.main} !important`,
          [`& .${CLOSE_BUTTON_CLASS} svg`]: {
            color: theme.palette.warning.main,
          },
        },
        '.notistack-MuiContent-info': {
          color: `${theme.palette.getContrastText(theme.palette.info.main)} !important`,
          backgroundColor: `${theme.palette.info.main} !important`,
          [`& .${CLOSE_BUTTON_CLASS} svg`]: {
            color: theme.palette.info.main,
          },
        },
      }}
    />
  );
}

/**
 * Snackbar provider that wraps notistack with TreePlotter styling and a custom context.
 * Wrap your app (or the part that needs notifications) with this provider, then use useSnackbar() in children.
 */
export function SnackbarProvider({
  children,
  maxSnack = 5,
  autoHideDuration = 3000,
  anchorOrigin = { vertical: 'top', horizontal: 'center' },
  dense = true,
  preventDuplicate = true,
}: SnackbarProviderProps) {
  return (
    <>
      <StyledNotistack />
      <NotistackProvider
        dense={dense}
        maxSnack={maxSnack}
        preventDuplicate={preventDuplicate}
        autoHideDuration={autoHideDuration}
        variant="success"
        anchorOrigin={anchorOrigin}
        iconVariant={{
          info: <SnackbarIcon icon={InfoIcon} />,
          success: <SnackbarIcon icon={CheckCircleIcon} />,
          warning: <SnackbarIcon icon={WarningIcon} />,
          error: <SnackbarIcon icon={ErrorIcon} />,
        }}
      >
        <CustomSnackbarProvider>{children}</CustomSnackbarProvider>
      </NotistackProvider>
    </>
  );
}

export default SnackbarProvider;
